import type { Activity, Deal, ForecastCategory } from '@/constants/crm'
import type { Quarter } from '@/lib/dates'

import { CLOSED_STAGES, stageMeta } from '@/constants/crm'

export function indexById<T extends { id: string }>(items: T[]): Map<string, T> {
    return new Map(items.map(item => [item.id, item]))
}

export function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean)

    if (parts.length === 0) return '?'

    return parts.slice(0, 2).map(part => part[0]?.toUpperCase() ?? '').join('')
}

export function isOpenDeal(deal: Deal): boolean {
    return !CLOSED_STAGES.includes(deal.stage)
}

export function isSlipping(deal: Deal, today: string): boolean {
    return isOpenDeal(deal) && deal.closeDate < today
}

export function weightedValue(deal: Deal): number {
    return Math.round(deal.value * stageMeta(deal.stage).probability / 100)
}

export type ForecastTotals = Record<Exclude<ForecastCategory, 'lost'>, number> & {
    weighted: number
    count: number
}

export function emptyTotals(): ForecastTotals {
    return { won: 0, commit: 0, bestCase: 0, pipeline: 0, weighted: 0, count: 0 }
}

function addDeal(totals: ForecastTotals, deal: Deal) {
    const category = stageMeta(deal.stage).category

    if (category === 'lost') return

    totals[category] += deal.value
    totals.weighted += weightedValue(deal)
    totals.count += 1
}

export type QuarterForecast = {
    totals: ForecastTotals
    months: Array<{ month: string, totals: ForecastTotals }>
    owners: Array<{ owner: string, totals: ForecastTotals }>
    deals: Deal[]
}

export function forecastForQuarter(deals: Deal[], quarter: Quarter): QuarterForecast {
    const inQuarter = deals.filter(deal => deal.closeDate >= quarter.start && deal.closeDate <= quarter.end)
    const totals = emptyTotals()
    const months = quarter.months.map(month => ({ month, totals: emptyTotals() }))
    const owners = new Map<string, ForecastTotals>()

    for (const deal of inQuarter) {
        addDeal(totals, deal)

        const month = months.find(item => deal.closeDate.startsWith(item.month))

        if (month) addDeal(month.totals, deal)

        const ownerTotals = owners.get(deal.owner) ?? emptyTotals()

        addDeal(ownerTotals, deal)
        owners.set(deal.owner, ownerTotals)
    }

    return {
        totals,
        months,
        owners: [...owners.entries()]
            .map(([owner, ownerTotals]) => ({ owner, totals: ownerTotals }))
            .filter(row => row.totals.count > 0)
            .sort((a, b) => b.totals.weighted - a.totals.weighted),
        deals: inQuarter
    }
}

export type ActivityGroup = 'overdue' | 'today' | 'upcoming' | 'completed'

export function activityGroup(activity: Activity, today: string): ActivityGroup {
    if (activity.completed) return 'completed'
    if (activity.dueDate < today) return 'overdue'
    if (activity.dueDate === today) return 'today'

    return 'upcoming'
}
